/**
 * Marketing & Automation Hub
 * Main dashboard and navigation hub for all marketing features
 */

import React, { useState, useEffect } from 'react';
import {
  Mail, Target, Zap, Tag, Settings, BarChart3,
  Users, Send, Clock, TrendingUp, CheckCircle,
  Megaphone, ArrowRight, Activity, ChevronRight,
  Layers, MousePointer, BarChart2, Eye,
  PlusCircle, Play, Filter, Bell, Globe
} from 'lucide-react';
import './MarketingHub.css';
import { useAccessControl } from '../../App';

// Sub-components
import SegmentsDashboard from './segments/SegmentsDashboard';
import CampaignsDashboard from './campaigns/CampaignsDashboard';
import AutomationDashboard from './automation/AutomationDashboard';
import TagManagement from './tags/TagManagement';
import MarketingSettings from './settings/MarketingSettings';
import ContactsManagement from './contacts/ContactsManagement';
import PipelineBoard from './pipeline/PipelineBoard';
import MarketplaceManagement from './marketplace/MarketplaceManagement';

function MarketingHub() {
  const { apiWithAccessControl } = useAccessControl();
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    totalSegments: 0,
    activeCampaigns: 0,
    automationRules: 0,
    marketingOptIns: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [segRes, autoRes] = await Promise.allSettled([
        apiWithAccessControl('/admin/segments'),
        apiWithAccessControl('/admin/automation/stats')
      ]);

      const newStats = { ...stats };

      if (segRes.status === 'fulfilled') {
        const segData = await segRes.value.json();
        if (segData.success) {
          newStats.totalSegments = segData.segments?.length || 0;
        }
      }

      if (autoRes.status === 'fulfilled') {
        const autoData = await autoRes.value.json();
        if (autoData.success && autoData.data) {
          newStats.automationRules = autoData.data.totalRules || 0;
        }
      }

      setStats(newStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const NAV_ITEMS = [
    { id: 'overview', label: 'Overview', icon: BarChart3, section: null },
    { id: 'divider-1', type: 'divider', label: 'Audience' },
    { id: 'contacts', label: 'Contacts', icon: Users, section: 'Audience' },
    { id: 'segments', label: 'Segments', icon: Target, section: 'Audience' },
    { id: 'tags', label: 'Tags & Labels', icon: Tag, section: 'Audience' },
    { id: 'divider-2', type: 'divider', label: 'Campaigns' },
    { id: 'campaigns', label: 'Email Campaigns', icon: Send, section: 'Campaigns' },
    { id: 'divider-3', type: 'divider', label: 'Sales' },
    { id: 'pipeline', label: 'Pipeline', icon: Layers, section: 'Sales' },
    { id: 'marketplace', label: 'Marketplace', icon: Globe, section: 'Sales' },
    { id: 'divider-4', type: 'divider', label: 'Automation' },
    { id: 'automation', label: 'Rules & Workflows', icon: Zap, section: 'Automation' },
    { id: 'divider-5', type: 'divider', label: 'Settings' },
    { id: 'settings', label: 'Preferences', icon: Settings, section: 'Settings' },
  ];

  const OverviewDashboard = () => (
    <div className="mkt-overview">
      {/* Hero Header */}
      <div className="mkt-hero">
        <div className="mkt-hero-content">
          <div className="mkt-hero-badge">
            <Megaphone size={14} />
            <span>Marketing Hub</span>
          </div>
          <h1 className="mkt-hero-title">Marketing & Automation</h1>
          <p className="mkt-hero-desc">Segment your audience, run campaigns, and automate workflows to grow your platform.</p>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="mkt-metrics">
        <div className="mkt-metric">
          <div className="mkt-metric-icon mkt-metric-icon--teal">
            <Target size={20} />
          </div>
          <div className="mkt-metric-info">
            <span className="mkt-metric-number">{stats.totalSegments}</span>
            <span className="mkt-metric-label">Segments</span>
          </div>
        </div>

        <div className="mkt-metric">
          <div className="mkt-metric-icon mkt-metric-icon--blue">
            <Send size={20} />
          </div>
          <div className="mkt-metric-info">
            <span className="mkt-metric-number">{stats.activeCampaigns}</span>
            <span className="mkt-metric-label">Campaigns</span>
          </div>
        </div>

        <div className="mkt-metric">
          <div className="mkt-metric-icon mkt-metric-icon--violet">
            <Zap size={20} />
          </div>
          <div className="mkt-metric-info">
            <span className="mkt-metric-number">{stats.automationRules}</span>
            <span className="mkt-metric-label">Automation Rules</span>
          </div>
        </div>

        <div className="mkt-metric">
          <div className="mkt-metric-icon mkt-metric-icon--amber">
            <CheckCircle size={20} />
          </div>
          <div className="mkt-metric-info">
            <span className="mkt-metric-number">{stats.marketingOptIns}</span>
            <span className="mkt-metric-label">Opt-ins</span>
          </div>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="mkt-features">
        <h2 className="mkt-features-heading">Get Started</h2>
        <div className="mkt-features-grid">
          <button className="mkt-feature-card" onClick={() => setActiveSection('segments')}>
            <div className="mkt-feature-top">
              <div className="mkt-feature-icon mkt-feature-icon--teal">
                <Filter size={22} />
              </div>
              <ChevronRight size={18} className="mkt-feature-arrow" />
            </div>
            <h3 className="mkt-feature-title">Build Segments</h3>
            <p className="mkt-feature-desc">Group merchants by tier, revenue, signup date, or custom rules.</p>
          </button>

          <button className="mkt-feature-card" onClick={() => setActiveSection('campaigns')}>
            <div className="mkt-feature-top">
              <div className="mkt-feature-icon mkt-feature-icon--blue">
                <Mail size={22} />
              </div>
              <ChevronRight size={18} className="mkt-feature-arrow" />
            </div>
            <h3 className="mkt-feature-title">Email Campaigns</h3>
            <p className="mkt-feature-desc">Send targeted emails to segments with rich content and variables.</p>
          </button>

          <button className="mkt-feature-card" onClick={() => setActiveSection('automation')}>
            <div className="mkt-feature-top">
              <div className="mkt-feature-icon mkt-feature-icon--violet">
                <Play size={22} />
              </div>
              <ChevronRight size={18} className="mkt-feature-arrow" />
            </div>
            <h3 className="mkt-feature-title">Automation Rules</h3>
            <p className="mkt-feature-desc">Set up trigger-based workflows that run automatically on events.</p>
          </button>

          <button className="mkt-feature-card" onClick={() => setActiveSection('pipeline')}>
            <div className="mkt-feature-top">
              <div className="mkt-feature-icon mkt-feature-icon--amber">
                <Layers size={22} />
              </div>
              <ChevronRight size={18} className="mkt-feature-arrow" />
            </div>
            <h3 className="mkt-feature-title">Sales Pipeline</h3>
            <p className="mkt-feature-desc">Track merchant acquisition with a Kanban board and opportunity management.</p>
          </button>

          <button className="mkt-feature-card" onClick={() => setActiveSection('tags')}>
            <div className="mkt-feature-top">
              <div className="mkt-feature-icon mkt-feature-icon--amber">
                <Tag size={22} />
              </div>
              <ChevronRight size={18} className="mkt-feature-arrow" />
            </div>
            <h3 className="mkt-feature-title">Tags & Labels</h3>
            <p className="mkt-feature-desc">Organize merchants with custom tags for easy filtering and targeting.</p>
          </button>
        </div>
      </div>

      {/* Activity */}
      <div className="mkt-recent">
        <h2 className="mkt-features-heading">Recent Activity</h2>
        <div className="mkt-recent-card">
          <div className="mkt-recent-empty">
            <div className="mkt-recent-empty-icon">
              <Activity size={24} />
            </div>
            <p className="mkt-recent-empty-text">No recent marketing activity</p>
            <span className="mkt-recent-empty-hint">Activity from campaigns, automation, and segments will appear here.</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mkt-hub">
      {/* Sidebar */}
      <aside className="mkt-sidebar">
        <div className="mkt-sidebar-brand">
          <div className="mkt-brand-icon">
            <Megaphone size={18} />
          </div>
          <span className="mkt-brand-text">Marketing</span>
        </div>

        <nav className="mkt-nav">
          {NAV_ITEMS.map((item) => {
            if (item.type === 'divider') {
              return (
                <div key={item.id} className="mkt-nav-group">
                  <span>{item.label}</span>
                </div>
              );
            }

            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <button
                key={item.id}
                className={`mkt-nav-link ${isActive ? 'mkt-nav-link--active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <Icon size={17} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Mobile Nav */}
      <div className="mkt-mobile-nav">
        {NAV_ITEMS.filter(i => !i.type).map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              className={`mkt-mobile-tab ${isActive ? 'mkt-mobile-tab--active' : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <Icon size={16} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <main className="mkt-content">
        {activeSection === 'overview' && <OverviewDashboard />}
        {activeSection === 'contacts' && <ContactsManagement apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'pipeline' && <PipelineBoard apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'marketplace' && <MarketplaceManagement apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'segments' && <SegmentsDashboard apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'campaigns' && <CampaignsDashboard apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'automation' && <AutomationDashboard apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'tags' && <TagManagement apiWithAccessControl={apiWithAccessControl} />}
        {activeSection === 'settings' && <MarketingSettings apiWithAccessControl={apiWithAccessControl} />}
      </main>
    </div>
  );
}

export default MarketingHub;
